// Нова ниша › data. The wizard never spends quota itself: it inserts drafts (`active=false,
// approved_at=null`, which discovery.selectWork never picks), flips them on only when the owner
// approves, and leaves the actual run to the nightly or to an explicit "Пусни откриване сега".

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../../lib/supabase.js';

const PAGE = 1000;

/** Every query_text in search_queries, across verticals: the global unique the generator respects. */
export function useExistingQueryTexts() {
  return useQuery({
    queryKey: ['search_query_texts'],
    queryFn: async (): Promise<string[]> => {
      if (!isConfigured || isDemoMode) {
        return ['зъболекар Бургас', 'дентална клиника Бургас Лазур'];
      }
      const texts: string[] = [];
      for (let page = 0; page < 20; page++) {
        const { data, error } = await supabase
          .from('search_queries')
          .select('query_text')
          .order('id')
          .range(page * PAGE, page * PAGE + PAGE - 1);
        if (error) throw new Error(error.message);
        const rows = (data ?? []) as { query_text: string }[];
        for (const row of rows) texts.push(row.query_text);
        if (rows.length < PAGE) break;
      }
      return texts;
    },
  });
}

function invalidateAll(client: ReturnType<typeof useQueryClient>): void {
  for (const key of [
    'search_queries',
    'search_query_texts',
    'verticals',
    'cities',
    'vertical_counts',
    'niche_context',
  ]) {
    void client.invalidateQueries({ queryKey: [key] });
  }
}

/** Upsert with ignoreDuplicates: a text another vertical already owns is skipped, not billed twice. */
export function useSaveDraftBatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (rows: readonly Record<string, unknown>[]) => {
      if (rows.length === 0) throw new Error('Няма избрани заявки за запис.');
      if (!isConfigured || isDemoMode) return rows.length;
      const { error } = await supabase
        .from('search_queries')
        .upsert([...rows], { onConflict: 'query_text', ignoreDuplicates: true });
      if (error) throw new Error(error.message);
      return rows.length;
    },
    onSuccess: () => invalidateAll(client),
  });
}

export interface ApproveInput {
  batchId: string;
  verticalKey: string;
  cityKey: string;
  activateVertical: boolean;
  activateCity: boolean;
}

export function useApproveBatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApproveInput) => {
      if (!isConfigured || isDemoMode) return;
      const approved = await supabase
        .from('search_queries')
        .update({ active: true, approved_at: new Date().toISOString() })
        .eq('batch_id', input.batchId);
      if (approved.error) throw new Error(approved.error.message);

      if (input.activateVertical) {
        const { error } = await supabase
          .from('verticals')
          .update({ active: true })
          .eq('key', input.verticalKey);
        if (error)
          throw new Error(`Заявките са одобрени, но нишата не се активира: ${error.message}`);
      }
      if (input.activateCity) {
        const { error } = await supabase
          .from('cities')
          .update({ active: true })
          .eq('key', input.cityKey);
        if (error)
          throw new Error(`Заявките са одобрени, но градът не се активира: ${error.message}`);
      }
    },
    onSuccess: () => invalidateAll(client),
  });
}
